import type { FC } from "react";
import { CalculatorRateComparison } from "web_myplann/Calculators/Components/CalculatorRateComparison";
import { CalculatorResultSection } from "web_myplann/Calculators/Components/CalculatorResultSection";
import { RefinancingResultHero } from "web_myplann/Calculators/Components/RefinancingResultHero";
import { useYearsLabel } from "web_myplann/Calculators/Hooks/useYearsLabel";
import type { CalculatorLeadState } from "web_myplann/Calculators/Types/CalculatorLeadState";
import { RefinancingOutcome } from "web_myplann/Calculators/Types/RefinancingOutcome";
import type { RefinancingPlan } from "web_myplann/Calculators/Types/RefinancingPlan";
import { mapRefinancingRateBars } from "web_myplann/Calculators/Utils/mapRefinancingRateBars";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { formatCurrency } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { CurrencyCode } from "web_myplann/Shared/constants";

type Props = {
	plan: RefinancingPlan;
	rate: number;
	clientRate: number;
	lead: CalculatorLeadState;
};

export const RefinancingResultSection: FC<Props> = ({
	plan,
	rate,
	clientRate,
	lead,
}) => {
	const { t } = useResource();
	const yearsLabel = useYearsLabel();
	const rateBars = mapRefinancingRateBars(plan.bankRates, clientRate, {
		yourRate: t(Resources.Calculators.Result.YourRate),
		betterCount: (count) =>
			t(Resources.Calculators.Result.BetterCount, { count }),
		worseCount: (count) =>
			t(Resources.Calculators.Result.WorseCount, { count }),
	});

	return (
		<CalculatorResultSection
			badge={
				plan.outcome === RefinancingOutcome.Saving
					? t(Resources.Calculators.Result.Badges.monthlySaving)
					: t(Resources.Calculators.Result.Badges.comparisonResult)
			}
			hero={
				<RefinancingResultHero
					saving={plan.saving}
					rate={rate}
					outcome={plan.outcome}
				/>
			}
			rows={[
				{
					label: t(Resources.Calculators.Refinancing.ResultNewInstalment),
					value: formatCurrency(plan.newMonthlyPayment, CurrencyCode.CZK, 0),
				},
				{
					label: t(Resources.Calculators.Refinancing.ResultRemaining),
					value: yearsLabel(plan.remainingYears),
				},
			]}
			comparison={
				rateBars.length > 0 && (
					<CalculatorRateComparison
						bars={rateBars}
						intro={t(Resources.Calculators.Result.CompareIntroYourRate, {
							count: plan.bankRates.length,
						})}
						variant="clientRate"
					/>
				)
			}
			lead={lead}
		/>
	);
};
